package one.healthium.uscreen.ui

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import androidx.lifecycle.viewModelScope
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import one.healthium.base.preferences.AppPreferences
import one.healthium.base.utils.kgToLb
import one.healthium.base.utils.toHeightString
import one.healthium.domain.user.UserInteractor
import one.healthium.domain.user.models.AdditionalUserInfoModel
import one.healthium.domain.user.models.Gender
import one.healthium.domain.user.models.UserProfileModel

import one.healthium.domain.user.models.bmi
import one.healthium.domain.user.models.losePercent
import one.healthium.domain.user.models.maxRecommendedWeight
import one.healthium.domain.user.models.minRecommendedWeight
import one.healthium.domain.user.models.prognoseWeightLoss
import one.healthium.domain.user.models.PersonalPlanPeriod
import one.healthium.domain.user.models.targetBmi
import one.healthium.domain.user.models.weightToLose
import one.healthium.ui.common.vm.BaseViewModel
import one.healthium.uscreen.domain.models.UScreenFlowModel
import one.healthium.base.utils.localizedName
import kotlin.math.roundToInt
import kotlin.time.Instant

class UScreenViewModel(
    private val userInteractor: UserInteractor,
    appPreferences: AppPreferences,
) : BaseViewModel() {

    private val _answers = MutableStateFlow<Map<String, UScreenAnswer>>(emptyMap())
    val answers: StateFlow<Map<String, UScreenAnswer>> = _answers.asStateFlow()

    private var flowModel: UScreenFlowModel? = null
    private var profileLoaded = false

    fun setFlowModel(model: UScreenFlowModel) {
        flowModel = model
        if (!profileLoaded) {
            profileLoaded = true
            loadAnswersFromProfile()
        }
    }

    private fun loadAnswersFromProfile() {
        launch {
            try {
                val user = userInteractor.getUser()
                val profile = user.profile
                co.touchlab.kermit.Logger.d("UScreen: loading profile: name=${profile.name}, age=${profile.age}, weight=${profile.weight}, height=${profile.height}, gender=${profile.gender}, additionalInfo=${profile.additionalInfo.keys}")
                val loaded = mutableMapOf<String, UScreenAnswer>()

                profile.name?.let { loaded["name"] = UScreenAnswer.TextAnswer(it) }
                profile.age?.let { loaded["age"] = UScreenAnswer.NumberAnswer(it.toFloat()) }
                profile.weight?.let { loaded["weight"] = UScreenAnswer.NumberAnswer(it) }
                profile.targetWeight?.let { loaded["target_weight"] = UScreenAnswer.NumberAnswer(it) }
                profile.height?.let { loaded["height"] = UScreenAnswer.NumberAnswer(it) }
                profile.gender?.let {
                    loaded["gender"] = UScreenAnswer.ChoiceAnswer(listOf(it.key))
                }
                profile.additionalInfo.forEach { (key, items) ->
                    loaded[key] = UScreenAnswer.ChoiceAnswer(items.map { it.key })
                }

                co.touchlab.kermit.Logger.d("UScreen: loaded ${loaded.size} answers: ${loaded.keys}")
                if (loaded.isNotEmpty()) {
                    _answers.update { current -> loaded + current }
                }
            } catch (e: Exception) {
                co.touchlab.kermit.Logger.e("Failed to load profile", e)
            }
        }
    }

    val placeholders: StateFlow<Map<String, String>> = combine(
        _answers,
        appPreferences.isMetricFlow,
    ) { answers, isMetric ->
        computePlaceholders(answers, isMetric)
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyMap())

    fun onChoiceSelected(key: String, optionKey: String, single: Boolean) {
        _answers.update { current ->
            val existing = current[key] as? UScreenAnswer.ChoiceAnswer
            val newKeys = if (single) {
                listOf(optionKey)
            } else {
                val currentKeys = existing?.selectedKeys.orEmpty()
                if (optionKey in currentKeys) currentKeys - optionKey else currentKeys + optionKey
            }
            current + (key to UScreenAnswer.ChoiceAnswer(newKeys))
        }
    }

    fun onTextChanged(key: String, value: String) {
        _answers.update { it + (key to UScreenAnswer.TextAnswer(value)) }
    }

    fun onNumberChanged(key: String, value: Float) {
        _answers.update { it + (key to UScreenAnswer.NumberAnswer(value)) }
    }

    fun isKeyFilled(key: String, answers: Map<String, UScreenAnswer>): Boolean {
        return when (val answer = answers[key]) {
            is UScreenAnswer.ChoiceAnswer -> answer.selectedKeys.isNotEmpty()
            is UScreenAnswer.TextAnswer -> answer.text.isNotBlank()
            is UScreenAnswer.NumberAnswer -> true
            null -> false
        }
    }

    fun persistAnswers() {
        launch {
            try {
                val current = _answers.value
                val existing = userInteractor.getUser().profile
                val updated = buildUserProfile(current)

                // Merge: only overwrite fields that have actual answers
                val merged = existing.copy(
                    name = updated.name ?: existing.name,
                    age = updated.age ?: existing.age,
                    weight = updated.weight ?: existing.weight,
                    targetWeight = updated.targetWeight ?: existing.targetWeight,
                    height = updated.height ?: existing.height,
                    gender = updated.gender ?: existing.gender,
                    additionalInfo = existing.additionalInfo + updated.additionalInfo,
                )
                userInteractor.updateUserProfile(merged)
            } catch (e: Exception) {
                co.touchlab.kermit.Logger.e("Failed to persist answers", e)
            }
        }
    }

    private fun buildUserProfile(answers: Map<String, UScreenAnswer>): UserProfileModel {
        return UserProfileModel(
            name = (answers["name"] as? UScreenAnswer.TextAnswer)?.text,
            age = (answers["age"] as? UScreenAnswer.NumberAnswer)?.value?.toInt(),
            weight = (answers["weight"] as? UScreenAnswer.NumberAnswer)?.value,
            targetWeight = (answers["target_weight"] as? UScreenAnswer.NumberAnswer)?.value,
            height = (answers["height"] as? UScreenAnswer.NumberAnswer)?.value,
            gender = (answers["gender"] as? UScreenAnswer.ChoiceAnswer)?.selectedKeys?.firstOrNull()?.let {
                Gender.fromKey(it)
            },
            email = null,
            avatarUrl = null,
            additionalInfo = buildAdditionalInfo(answers),
            additionalText = emptyMap(),
            statements = emptyMap(),
        )
    }

    private fun buildAdditionalInfo(answers: Map<String, UScreenAnswer>): Map<String, List<AdditionalUserInfoModel>> {
        val result = mutableMapOf<String, List<AdditionalUserInfoModel>>()
        answers.forEach { (key, answer) ->
            if (answer is UScreenAnswer.ChoiceAnswer) {
                result[key] = answer.selectedKeys.map { optionKey ->
                    val altText = findAltText(key, optionKey)
                    AdditionalUserInfoModel(key = optionKey, text = optionKey, altText = altText)
                }
            }
        }
        return result
    }

    private fun findAltText(blockKey: String, optionKey: String): String? {
        val model = flowModel ?: return null
        for (group in model.groups) {
            for (screen in group.screens) {
                for (block in screen.blocks) {
                    if (block is one.healthium.uscreen.domain.models.ChoiceListBlock && block.key == blockKey) {
                        return block.options.find { it.key == optionKey }?.altText
                    }
                }
            }
        }
        return null
    }

    private fun computePlaceholders(answers: Map<String, UScreenAnswer>, isMetric: Boolean): Map<String, String> {
        val profile = buildUserProfile(answers)
        val map = mutableMapOf<String, String>()

        profile.name?.let { map["name"] = it }
        profile.age?.let {
            map["age"] = it.toString()
            map["down_years"] = ((it / 10) * 10).toString()
        }

        profile.weight?.let { w ->
            map["weight"] = formatWeight(w, isMetric)
        }
        profile.targetWeight?.let { tw ->
            map["target_weight"] = formatWeight(tw, isMetric)
        }
        profile.height?.let { h ->
            map["height"] = h.toHeightString(isMetric)
        }

        profile.gender?.let { g ->
            map["gender"] = if (g == Gender.Female) "women" else "men"
        }

        profile.bmi?.let { map["bmi"] = it.toString() }
        profile.targetBmi?.let { map["target_bmi"] = it.toString() }
        profile.losePercent?.let { map["lose_percent"] = "$it%" }
        profile.minRecommendedWeight?.let { map["min_recommended_weight"] = formatWeight(it, isMetric) }
        profile.maxRecommendedWeight?.let { map["max_recommended_weight"] = formatWeight(it, isMetric) }
        profile.weightToLose?.let { map["weight_to_lose"] = formatWeight(it, isMetric) }

        profile.prognoseWeightLoss(PersonalPlanPeriod.In3month)?.let {
            map["prognose_weight_3month"] = formatWeight(it, isMetric)
        }
        profile.prognoseWeightLoss(PersonalPlanPeriod.In3monthShort)?.let {
            map["prognose_weight_3month_short"] = formatWeight(it, isMetric)
        }
        profile.prognoseWeightLoss(PersonalPlanPeriod.In21days)?.let {
            map["prognose_weight_21"] = formatWeight(it, isMetric)
        }

        val nowMs = kotlin.time.Clock.System.now().toEpochMilliseconds()
        val dayMs = 86_400_000L
        fun formatDate(daysToAdd: Int): String {
            val instant = Instant.fromEpochMilliseconds(nowMs + daysToAdd * dayMs)
            val dt = instant.toLocalDateTime(TimeZone.currentSystemDefault())
            return "${dt.month.localizedName(abbreviated = true)} ${dt.day}"
        }
        map["in_21days"] = formatDate(21)
        map["in_3months"] = formatDate(90)
        map["in_3months_short"] = formatDate(75)

        // Custom placeholders from choice altText
        profile.additionalInfo.forEach { (key, items) ->
            val altText = items.firstOrNull()?.altText
            if (altText != null) {
                map[key] = altText
            }
        }

        return map
    }

    private fun formatWeight(kg: Float, isMetric: Boolean): String {
        return if (isMetric) {
            "${kg.roundToInt()} kg"
        } else {
            "${kg.kgToLb().roundToInt()} lbs"
        }
    }
}
